<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { NEmpty, NSelect, NSpin, NTabPane, NTabs } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import ProductOwnerRunCard from './ProductOwnerRunCard.vue'
import ProductOwnerActivityTab from './ProductOwnerActivityTab.vue'
import ProductOwnerReviewTab from './ProductOwnerReviewTab.vue'
import { useProductOwnerRunsStore } from '../stores/product_owner_runs'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { PRODUCT_OWNER_RUN_STATUSES, productOwnerRunStatusLabel } from '../composables/productOwnerRunStatus'

// Read-only run-history page (#916) for the autonomous product-owner
// heartbeat (#895/#905). Card-based v-for list (not a <table>) — the
// established pattern for admin list pages in this codebase
// (AdminAgentRepos.vue, AdminShares.vue): naturally responsive/stacked
// on mobile without a separate layout, unlike a wide table with a prose
// summary column that has nowhere good to go on a narrow screen.
//
// #925 adds a repo/status filter and per-card expand/collapse (delegated
// to ProductOwnerRunCard.vue) so the list stays scannable as run history
// grows past a screenful.
//
// #1070 widened the page to max-w-6xl (instead of every other admin
// page's max-w-3xl) so "Latest per repo" could lay its cards out as a
// responsive grid — but kept the filters and History list at max-w-3xl
// in their own centered wrapper, on the theory that a scrolling log of
// individual runs doesn't benefit from extra width. #1078 walked that
// back: on a wide desktop viewport the max-w-6xl outer section itself
// still reads as a large margin, and nesting max-w-3xl inside it made
// History look like it was using half the page. Both wrappers now just
// take the full width of their parent (no max-w, no mx-auto) so every
// section — filters, Latest per repo, History — spans the same width.
// The filters stay above both sections (previously they sat between
// "Latest per repo" and "History") so isolating one repo's activity
// doesn't require scrolling past the unfiltered overview first.
const store = useProductOwnerRunsStore()
useDocumentTitle('Product-owner activity')

// Matches AdminAgentHostStats.vue's own polling cadence.
const STATUS_POLL_MS = 15_000
let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(async () => {
  try {
    await store.fetchRuns()
  } catch {
    /* loadError surfaces in template */
  }
  pollTimer = setInterval(() => {
    void store.fetchRuns().catch(() => {})
  }, STATUS_POLL_MS)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const tab = ref<'runs' | 'activity' | 'review'>('runs')

// naive-ui's NSelect option `value` can't be `null`/`undefined` (see
// PromptLibrary.vue's GLOBAL_SCOPE comment) — 'all' is the sentinel for
// "no filter" on both dropdowns.
const ALL = 'all'
const selectedRepo = ref<string>(ALL)
const selectedStatus = ref<string>(ALL)

// Distinct repos actually present in the run history, so this holds up
// once a second repo opts in alongside fpl-stats — no hardcoded list.
const repoOptions = computed(() => {
  const repos = Array.from(new Set(store.runs.map((r) => r.repo))).toSorted()
  return [{ label: 'All repos', value: ALL }, ...repos.map((repo) => ({ label: repo, value: repo }))]
})

const statusOptions = [
  { label: 'All statuses', value: ALL },
  ...PRODUCT_OWNER_RUN_STATUSES.map((status) => ({ label: productOwnerRunStatusLabel(status), value: status })),
]

const filteredRuns = computed(() =>
  store.runs.filter(
    (run) =>
      (selectedRepo.value === ALL || run.repo === selectedRepo.value) &&
      (selectedStatus.value === ALL || run.status === selectedStatus.value),
  ),
)

// #1078: the repo/status filters above narrow "History" but previously left
// "Latest per repo" showing every opted-in repo regardless — so filtering to
// one repo, or to one status, didn't touch the overview cards at all. Same
// predicate as filteredRuns, applied to the (already server-computed)
// latest-per-repo set instead of the flat run list.
const filteredLatestPerRepo = computed(() =>
  store.latestPerRepo.filter(
    (run) =>
      (selectedRepo.value === ALL || run.repo === selectedRepo.value) &&
      (selectedStatus.value === ALL || run.status === selectedStatus.value),
  ),
)

// The most-recently-started run auto-expands on load (#925). Computed off
// the FULL run list (not filteredRuns) so switching filters never changes
// which run "counts" as latest — it only changes which cards are visible.
// Falls back to created_at when started_at is null (a run that errored
// before it began) so a just-failed row can still win the comparison.
const latestRunId = computed<number | null>(() => {
  let latest: { id: number; time: number } | null = null
  for (const run of store.runs) {
    const time = Date.parse(run.started_at ?? run.created_at)
    if (Number.isNaN(time)) continue
    if (!latest || time > latest.time) latest = { id: run.id, time }
  }
  return latest?.id ?? null
})
</script>

<template>
  <RouteShell>
    <section class="w-full flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="page-title">Product-owner activity</h1>
        <p class="text-sm text-white/60 m-0">
          Recent heartbeat runs for the autonomous product-owner agent (#895). Refreshes automatically every
          {{ STATUS_POLL_MS / 1000 }}s.
        </p>
      </header>

      <NTabs v-model:value="tab" type="segment" animated>
        <NTabPane name="runs" tab="Runs">
          <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
            <NSpin />
          </div>

          <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="product-owner-runs-load-error">
            Failed to load product-owner runs: {{ store.loadError }}
          </div>

          <NEmpty
            v-else-if="store.runs.length === 0"
            description="No product-owner runs yet."
            class="my-12"
            data-testid="product-owner-runs-empty"
          />

          <template v-else>
            <!-- Filters lead the tab (#1070) — isolating one repo's activity
                 no longer means scrolling past the unfiltered overview below. -->
            <div class="w-full grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="product-owner-runs-filters">
              <label class="flex flex-col gap-1 text-xs text-white/60">
                Repo
                <NSelect
                  v-model:value="selectedRepo"
                  :options="repoOptions"
                  data-testid="product-owner-runs-filter-repo"
                />
              </label>
              <label class="flex flex-col gap-1 text-xs text-white/60">
                Status
                <NSelect
                  v-model:value="selectedStatus"
                  :options="statusOptions"
                  data-testid="product-owner-runs-filter-status"
                />
              </label>
            </div>

            <!-- One card per repo — its actual latest run, so a repo that runs
                 less often can't be hidden by a noisier one dominating the
                 flat history list (#957) — narrowed by the same repo/status
                 filters as History (#1078), so filtering to one repo (or one
                 status) actually hides the other repos' overview cards too
                 instead of leaving them all visible regardless of the
                 filters. A responsive grid (#1070) rather than a single
                 column: this section is already one card per repo, so laying
                 them out side by side on desktop gives the quick overview a
                 stacked column can't. The section itself still keys off the
                 UNFILTERED `store.latestPerRepo` so it disappears only when
                 there's truly no data yet, not just because a filter
                 narrowed it to zero. -->
            <div v-if="store.latestPerRepo.length > 0" class="flex flex-col gap-2">
              <h2 class="text-sm font-semibold text-white/80 m-0">Latest per repo</h2>

              <NEmpty
                v-if="filteredLatestPerRepo.length === 0"
                description="No repos match the current filters."
                class="my-4"
                data-testid="product-owner-runs-latest-per-repo-filtered-empty"
              />

              <!-- #1083: fixed `sm:grid-cols-2 xl:grid-cols-3` capped this at 3
                   columns no matter how wide the viewport got — a 1920px
                   desktop was left with the same 3 cards-plus-margin as a
                   laptop. `minmax(min(400px,100%),1fr)` sizes tracks off the
                   card's own comfortable width instead of breakpoint guesses,
                   so a 1920px row fits 4; the `min(400px,100%)` half keeps a
                   lone column from overflowing on phones narrower than
                   400px, which plain `minmax(400px,1fr)` would do. `auto-fit`
                   (not `auto-fill`) is the other half of #1083: when the repo
                   filter above narrows this section to one card, auto-fit
                   collapses the would-be-empty tracks and lets that single
                   card stretch across the full row instead of sitting at
                   ~400px next to blank space — same rule handles the
                   "let it expand, it's the only card" ask with no extra
                   conditional markup. -->
              <ul
                v-else
                class="list-none p-0 m-0 grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(400px,100%),1fr))]"
                data-testid="product-owner-runs-latest-per-repo"
              >
                <ProductOwnerRunCard
                  v-for="run in filteredLatestPerRepo"
                  :key="run.id"
                  :run="run"
                  :default-expanded="true"
                />
              </ul>
            </div>

            <div class="w-full flex flex-col gap-4">
              <h2 class="text-sm font-semibold text-white/80 m-0">History</h2>

              <NEmpty
                v-if="filteredRuns.length === 0"
                description="No runs match the current filters."
                class="my-8"
                data-testid="product-owner-runs-filtered-empty"
              />

              <ul v-else class="list-none p-0 m-0 flex flex-col gap-4" data-testid="product-owner-runs-list">
                <ProductOwnerRunCard
                  v-for="run in filteredRuns"
                  :key="run.id"
                  :run="run"
                  :default-expanded="run.id === latestRunId"
                />
              </ul>
            </div>
          </template>
        </NTabPane>

        <!-- Activity (#1036/#1210) and Review (#1183) both own their own
             fetch and every loading/error/empty state internally (see
             ProductOwnerActivityTab.vue / ProductOwnerReviewTab.vue) since
             both also own selectors (repo/window) the parent has no reason
             to know about. -->
        <NTabPane name="activity" tab="Activity">
          <ProductOwnerActivityTab />
        </NTabPane>

        <NTabPane name="review" tab="Review">
          <ProductOwnerReviewTab />
        </NTabPane>
      </NTabs>
    </section>
  </RouteShell>
</template>
